import type { useTranslations } from "next-intl";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";

export function getErrorStatusFromUnknownError(error: unknown): string | null {
  if (typeof error === "object" && error !== null && "status" in error) {
    return (error as { status: string }).status;
  }
  return null;
}

export function getErrorMessageFromUnknownError(
  error: unknown,
  {
    defaultMessage = "Unknown error",
    emptyMessage = "Empty error",
  }: {
    defaultMessage?: string;
    emptyMessage?: string;
  } = {},
): string {
  if (!error) return emptyMessage;

  if (typeof error === "string") {
    return error;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "object") {
    if ("message" in error) {
      return error.message as string;
    }
    if ("detail" in error) {
      return (error.detail as { message: string }).message;
    }
  }

  return defaultMessage;
}

type TranslatorFunction = ReturnType<typeof useTranslations<"ClientKeys.Errors.Microphone">>;

interface HandleMicrophoneErrorConfig {
  handler: (title: string, description: string) => void;
  t: TranslatorFunction;
  demoType: string;
}

export function handleMicrophoneError(error: Error, config: HandleMicrophoneErrorConfig): boolean {
  const { handler, t, demoType } = config;

  if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
    captureEvent(
      EventName.mw_browser_permission_error,
      {
        error_name: "microphone_denied",
        demo_type: demoType,
      },
      { transport: "sendBeacon" },
    );

    handler(t("microphone_denied"), t("microphone_denied_description"));
    return true;
  } else if (error.name === "NotFoundError") {
    captureEvent(
      EventName.mw_browser_permission_error,
      {
        error_name: "no_microphone",
        demo_type: demoType,
      },
      { transport: "sendBeacon" },
    );

    handler(t("no_microphone"), t("no_microphone_description"));
    return true;
  }

  return false;
}
